import { parseZodErrorMessages } from "@@/backoffice/dashboard/shared/parse-zod-error-messages";
import { browserLogger } from "@@/settings";
import type { FormId } from "@@/shared/form/form-and-field-id";
import type { MutateOptions } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { type ZodSchema } from "zod";
import { useUpdateEffect } from "../use-update-effect";
import { useFormId } from "./form-id.context";
import { useForm3Components, type Form3Components } from "./form3-components";
import type { Form3ErrorMessage } from "./form3.error-message";
import type { InitializeForm3Params, TForm3 } from "./form3.form";
import type { Form3Actions } from "./form3.form-actions";
import { useForm3Store } from "./form3.zustand-store";

type UseFormParams<
    FormSchema extends ZodSchema,
    FormData extends Record<string, unknown>,
    FieldId,
> = {
    readonly formId?: FormId;
    readonly initializeIfNotExists?: InitializeForm3Params<
        FormSchema,
        FormData,
        FieldId
    >;
    readonly onFormChange?: (data: Partial<FormData>) => void;
    readonly afterUnmount?: () => void;
    readonly afterFormInitialized?: (data: Partial<FormData>) => void;
};

/**
 * Custom hook to manage form state using Zustand store.
 *
 * @template ZSchema - The Zod schema type for the form.
 * @param {UseFormParams<ZSchema>} params - Parameters for initializing and managing the form.
 * @returns {[Form<ZSchema>, Form3Actions<ZSchema>]} - Returns the form state and form actions.
 *
 * @throws {Error} If formId is not provided and no form id provider is available.
 * @throws {Error} If the form with the given id is not found.
 *
 * @example
 * const [form, actions] = useForm({
 *   formId: 'myForm',
 *   initializeIfNotExists: { `<initial form data>` },
 *   onFormChange: (data) => console.log('Form data changed', data),
 *   mutation: myMutationFunction,
 * });
 *
 * @remarks
 * - If `clearOnUnmount` is true, the form will be deleted from the store when the component unmounts.
 * - If `onFormChange` is provided, the function will be called whenever the form data changes.
 * - Use the `submit` action to post the form to the server if a mutation is provided.
 */
const useForm = <
    FormSchema extends ZodSchema,
    FormData extends Record<string, unknown>,
    FieldId extends string,
>(
    params: UseFormParams<FormSchema, FormData, FieldId>,
): readonly [
    TForm3<FormSchema, FormData, FieldId>,
    Form3Actions<FormData>,
    Form3Components<FormSchema, FormData>,
] => {
    const formIdFromContext = useFormId({ doNotThrow: true });
    const formId = params?.formId || formIdFromContext;
    const initializeIfNotExists = params?.initializeIfNotExists;
    //If the form was initiated from this hook
    const [_initiated, setInitiated] = useState(false);

    if (!formId)
        throw new Error(
            "Form id not given and no form id provider is available",
        );

    const init = useForm3Store(s => s.initializeForm);
    //Better name for this function
    const runInitIf = () => {
        if (initializeIfNotExists) {
            const initializedForm = init({
                ...initializeIfNotExists,
                formId,
            });
            setInitiated(true);
            params.afterFormInitialized?.(initializedForm.data);
            return initializedForm;
        }
        return undefined;
    };

    const form =
        useForm3Store(s => {
            const form = s.forms[formId];

            if (!form) {
                browserLogger.warn(`Form with id ${formId} not found`, {
                    store: s,
                });
                return undefined;
            }
            return form as TForm3<FormSchema, FormData, string>;
        }) ?? runInitIf();

    if (!form) throw new Error(`Form with id ${formId} not found`);
    const FormComponents: Form3Components<FormSchema, FormData> =
        useForm3Components<FormSchema, FormData>();

    // If clearOnUnmount is true, the form will be deleted from the
    // store when the component unmounts
    const afterUnmountRef = useRef(params.afterUnmount);
    afterUnmountRef.current = params.afterUnmount;
    useEffect(
        function deleteOnUnmount() {
            return () => {
                if (form?.clearOnUnmount) {
                    browserLogger.debug(`Deleting form with id ${formId}`);
                    useForm3Store.getState().deleteForm(formId);
                }
                // Call the afterUnmount function if provided
                // This is useful for cleaning up any side effects
                afterUnmountRef.current?.();
            };
        },
        [form?.clearOnUnmount, formId],
    );

    // If onFormChange is provided, the function will be called whenever
    // the form data changes
    useUpdateEffect(
        function callOnFormChange() {
            params.onFormChange?.(form?.data);
        },
        [form?.data],
    );

    // Use mutation to post the form to the server
    // if a mutation is provided
    const submit = useCallback(
        (
            options: {
                genericErrorMessage?: Form3ErrorMessage;
            } & MutateOptions<unknown, unknown, FormData, unknown> = {},
        ): Form3ErrorMessage[] => {
            const mutation = form.mutation;
            browserLogger.log("submit1", options, mutation);
            if (!mutation) throw new Error("No mutation provided");
            if (!form) throw new Error("Form not found");
            const { genericErrorMessage, ...mutationOptions } = options;
            const commandParseResult = form.safeParse();
            browserLogger.log("submit2", commandParseResult, form.data);

            if (!commandParseResult?.success) {
                return genericErrorMessage
                    ? [genericErrorMessage]
                    : parseZodErrorMessages(commandParseResult.error);
            }
            const command = commandParseResult.data;
            mutation.mutate(command, mutationOptions);
            return [];
        },
        [form],
    );

    const actions = useMemo(() => ({ submit }), [submit]);
    return useMemo(
        () => [form, actions, FormComponents] as const,
        [form, actions, FormComponents],
    );
};

export { useForm as useForm3 };
export type { UseFormParams as UseForm3Params };
